import { ChakraProps } from "@chakra-ui/system";
import { useTableContext } from "contexts/TableContext";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

interface Column<T> {
  columnKey: keyof T;
  header: string;
  render?: (
    data: T,
    updateItem: (idKey: keyof T, id: any, item: T) => void
  ) => ReactNode;
}

export interface Row<T> {
  href?: (row: T) => string;
  onClick?: (row: T) => void;
  rowStyle?: (row: T) => ChakraProps | void;
}

interface FilterDefinition {
  filterKey: string;
  label: string;
  type: "input" | "selectbox" | "checkbox" | "date" | "dateRange";
  options?: Array<{ label: string; value: any }>; // For selectbox type
  defaultValue?: any;
  placeholder?:string
}

interface UseTableParams<T> {
  columns: Column<T>[];
  filtersConfig?: FilterDefinition[];
  initialFilters?: Record<string, any>;
  fetchData: (params: {
    page: number;
    pageSize: number;
    sort?: string;
    filters?: Record<string, any>;
  }) => Promise<{ data: T[]; total: number }>;
  pageSize: number;
  page: number;
}

function useTable<T>({
  columns,
  fetchData,
  page,
  pageSize,
  filtersConfig,
  initialFilters,
}: UseTableParams<T>) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const { refreshCounter } = useTableContext();

  const tempInitialFilters = useMemo(
    () =>
      filtersConfig?.reduce((acc, curr) => {
        // check if the filter is already in the URL
        if (initialFilters && initialFilters[curr.filterKey]) {
          acc[curr.filterKey] = initialFilters[curr.filterKey];
          return acc;
        }

        acc[curr.filterKey] = curr.defaultValue ?? "";
        return acc;
      }, {} as Record<string, any>),
    [filtersConfig, initialFilters]
  );

  const [filters, setFilters] =
    useState<Record<string, any>>(tempInitialFilters);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, total } = await fetchData({ page, pageSize, filters });
        setData(data);
        setTotalRecords(total);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };
    if (refreshCounter) {
      fetch();
    }
  }, [fetchData, page, pageSize, filters, refreshCounter]);

  const updateFilter = useCallback((key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateItem = useCallback((idKey: keyof T, id: any, item: T) => {
    setData((prev) => {
      const index = prev.findIndex((i) => i[idKey] === id);
      if (index === -1) return prev;
      const newData = [...prev];
      newData[index] = item;
      return newData;
    });
  }, []);

  return {
    data,
    loading,
    error,
    totalRecords,
    updateFilter,
    filters,
    updateItem,
  };
}

export { useTable };
export type { Column, UseTableParams, FilterDefinition };
